import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Grid,
  useTheme,
  alpha,
  Rating,
  Tooltip,
} from '@mui/material';
import {
  LocationOn,
  Email,
  Phone,
  Description,
  Source,
  Edit,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { Candidate, statusColors, experienceLabels } from '../../types/Candidate';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../context/ThemeContext';

interface CandidateListItemProps {
  candidate: Candidate;
  onDelete: (id: number) => void;
}

const CandidateListItem: React.FC<CandidateListItemProps> = ({ candidate, onDelete }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isDark = mode === 'dark';

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const handleView = () => {
    navigate(`/candidates/${candidate.id}`);
  };

  const handleEdit = () => {
    navigate(`/candidates/${candidate.id}/edit`);
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 'none',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        '&:hover': {
          boxShadow: isDark 
            ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}`
            : `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease',
        },
      }}
    >
      <Box
        sx={{
          height: 6,
          width: '100%',
          backgroundColor: statusColors[candidate.status],
        }}
      />
      <CardContent sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Profile and basic info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 54,
                  height: 54,
                  bgcolor: isDark
                    ? alpha(statusColors[candidate.status], 0.2)
                    : alpha(statusColors[candidate.status], 0.1),
                  color: statusColors[candidate.status],
                  fontWeight: 'bold',
                  mr: 2,
                }}
              >
                {getInitials(candidate.name)}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {candidate.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                  {candidate.position}
                </Typography>
                <Chip
                  label={candidate.status}
                  size="small"
                  sx={{
                    textTransform: 'capitalize',
                    bgcolor: alpha(statusColors[candidate.status], 0.1),
                    color: statusColors[candidate.status],
                    fontWeight: 500,
                    mr: 1,
                    borderRadius: 1,
                  }}
                />
                <Chip
                  label={candidate.department}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Contact and details */}
          <Grid item xs={12} md={4}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Email fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">{candidate.email}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Phone fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">{candidate.phone}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LocationOn fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">{candidate.location}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Source fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">{candidate.source}</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Skills and actions */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', height: '100%' }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Experience: {experienceLabels[candidate.experience]}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Applied: {new Date(candidate.appliedDate).toLocaleDateString()}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>Rating:</Typography>
                  <Rating value={candidate.rating} readOnly size="small" />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {candidate.skills.slice(0, 3).map((skill) => (
                    <Chip
                      key={skill}
                      label={skill}
                      size="small"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        borderRadius: 1,
                        backgroundColor: isDark 
                          ? alpha(theme.palette.primary.main, 0.1) 
                          : alpha(theme.palette.primary.main, 0.05),
                      }}
                    />
                  ))}
                  {candidate.skills.length > 3 && (
                    <Chip
                      label={`+${candidate.skills.length - 3}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                <Box>
                  <Tooltip title="View Details">
                    <IconButton onClick={handleView} size="small" color="primary">
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton onClick={handleEdit} size="small" color="primary">
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => onDelete(candidate.id)} size="small" color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {candidate.resume && (
                  <Tooltip title="View Resume">
                    <Chip
                      icon={<Description fontSize="small" />}
                      label="Resume"
                      variant="outlined"
                      size="small"
                      clickable
                      sx={{ borderRadius: 1, height: 24 }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CandidateListItem;